/**
 * EsignConsentGate — ESIGN dual-consent gate per Reg E §1005.17.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Why this is a separate UI gate (not bundled into the signature capture):
 *   1. E-SIGN (15 USC §7001) requires explicit consent to electronic
 *      records *before* signature collection, distinct from the signature
 *      itself.
 *   2. Reg E §1005.17 reinforces this with a dual-consent mandate for
 *      overdraft opt-in — the customer must give one consent for the
 *      e-records channel and a separate consent for the opt-in.
 *
 * Behaviour:
 *   - On mount: query /v3/esign/status. If `hasConsent && !requiresFreshConsent`,
 *     emit `onPass()` immediately.
 *   - Otherwise: fetch the canonical consent text, render it scrollable,
 *     track scroll progress, enable the I-agree button only when the
 *     customer has scrolled past 80%.
 *   - On accept: POST /v3/esign/consent with the intent + signature
 *     timestamps, viewport, UA, and final scroll-progress.
 *   - On success: emit `onPass()`.
 *
 * Plain-language doctrine — title, body intro, button copy. The consent
 * body itself is server-provided.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type {
  EsignConsentRecordInput,
  EsignConsentTextResponse,
} from '@retail-ops/shared-types';

// ── Props ──────────────────────────────────────────────────────────────────

export interface EsignConsentGateProps {
  cartId: string;
  signerCifNumber: string;
  /** Used for audit context — surfaced in the consent intro copy too. */
  signerEmail: string;
  signerDisplayName: string;
  /** Called after the customer accepts (or if existing fresh consent is found). */
  onPass: () => void;
  /** Called when the customer picks "I prefer paper" or closes the gate. */
  onCancel: () => void;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const overlayS: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.65)',
  zIndex: 9100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

const cardS: CSSProperties = {
  background: 'var(--surface-card)',
  color: 'var(--ink-900)',
  borderRadius: 10,
  maxWidth: 720,
  width: '100%',
  maxHeight: 'min(90vh, 720px)',
  padding: 0,
  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
  display: 'flex',
  flexDirection: 'column',
};

const headerS: CSSProperties = {
  padding: '16px 24px',
  borderBottom: '1px solid var(--surface-border)',
  flex: '0 0 auto',
};

const titleS: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: 'var(--pcb-blue-dark)',
};

const introS: CSSProperties = {
  margin: '6px 0 0',
  fontSize: 13,
  color: 'var(--ink-700)',
  lineHeight: 1.45,
};

const progressBarWrapS: CSSProperties = {
  padding: '10px 24px 0',
  flex: '0 0 auto',
};

const progressBarTrackS: CSSProperties = {
  height: 6,
  background: 'var(--ink-200)',
  borderRadius: 999,
  overflow: 'hidden',
};

const progressBarFillS = (pct: number, ready: boolean): CSSProperties => ({
  width: `${Math.min(100, pct)}%`,
  height: '100%',
  background: ready ? 'var(--ok-fill)' : 'var(--brand-fill)',
  transition: 'width 120ms ease-out, background 240ms ease-out',
});

const progressLabelS: CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: 'var(--ink-700)',
};

const scrollerS: CSSProperties = {
  flex: '1 1 auto',
  overflowY: 'auto',
  padding: '14px 24px',
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--ink-900)',
  background: 'var(--surface-subtle)',
  whiteSpace: 'pre-wrap',
};

const loadingS: CSSProperties = {
  ...scrollerS,
  background: 'var(--surface-card)',
  color: 'var(--ink-700)',
  fontStyle: 'italic',
};

const errorS: CSSProperties = {
  background: 'var(--err-bg)',
  color: 'var(--err-text)',
  border: '1px solid var(--err-line)',
  padding: '10px 14px',
  borderRadius: 6,
  fontSize: 13,
  margin: '0 24px 12px',
};

const footerS: CSSProperties = {
  padding: '14px 24px',
  borderTop: '1px solid var(--surface-border)',
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  flex: '0 0 auto',
};

const primaryBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid var(--ok-fill)',
  background: 'var(--ok-fill)',
  color: 'var(--on-brand)',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const disabledBtnS: CSSProperties = {
  ...primaryBtnS,
  background: 'var(--ink-500)', // disabled
  border: '1px solid var(--input-border)',
  cursor: 'not-allowed',
};

const ghostBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid var(--surface-border)',
  background: 'var(--surface-card)',
  color: 'var(--ink-900)',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

// ── Constants ──────────────────────────────────────────────────────────────

const SCROLL_THRESHOLD_PCT = 80;

// Fallback copy when the server's `/v3/esign/text` endpoint is unreachable.
// This is intentionally generic and only kicks in if the backend fails — the
// real consent text lives server-side so Compliance can update it without a
// frontend redeploy.
const FALLBACK_CONSENT_BODY =
  'Electronic Records and Signatures Consent\n\n' +
  'Phelps County Bank will provide your account agreements, disclosures, ' +
  'periodic statements, notices, and other records electronically. By ' +
  'agreeing below, you consent to receive these records in electronic ' +
  'form and to sign documents electronically.\n\n' +
  'You may withdraw this consent at any time. Contact a Personal Banker ' +
  'to switch to paper delivery. Some records may not be available in ' +
  'paper form once your consent is in effect.\n\n' +
  'You are confirming you have a device capable of viewing PDFs and ' +
  'receiving email or text-message notifications about new records.';

// ── Component ──────────────────────────────────────────────────────────────

type GateState =
  | 'checking-status'
  | 'loading-text'
  | 'reading'
  | 'submitting'
  | 'error';

export function EsignConsentGate(props: EsignConsentGateProps): JSX.Element | null {
  const { cartId, signerCifNumber, signerDisplayName, onPass, onCancel } = props;

  const api = useApiClient();

  const intentTimestampRef = useRef<string>(new Date().toISOString());
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const isMountedRef = useRef<boolean>(true);

  const [state, setState] = useState<GateState>('checking-status');
  const [consentText, setConsentText] = useState<EsignConsentTextResponse | null>(
    null
  );
  const [scrollPct, setScrollPct] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // ── Lifecycle: status probe + text fetch ───────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    let cancelled = false;

    void (async () => {
      // 1) Probe status — short-circuit when fresh consent already exists.
      try {
        const status = await api.retail.signing.esign.getStatus({
          cartId,
          signerCifNumber,
        });
        if (cancelled) return;
        if (status.hasConsent && !status.requiresFreshConsent) {
          onPass();
          return;
        }
      } catch (err) {
        // Non-fatal: fall through to prompt-and-collect.
        // eslint-disable-next-line no-console
        console.warn('[EsignConsentGate] status probe failed', err);
      }

      if (cancelled) return;
      setState('loading-text');

      // 2) Fetch the canonical consent body. On failure, render the
      //    fallback copy and continue — the customer still gets a chance
      //    to consent, and the audit row will note the fallback source.
      try {
        const text = await api.retail.signing.esign.getText();
        if (cancelled) return;
        setConsentText(text);
        setState('reading');
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[EsignConsentGate] getText failed; using fallback', err);
        setConsentText({
          version: 'fallback-1',
          title: 'Electronic Records and Signatures Consent',
          body: FALLBACK_CONSENT_BODY,
        });
        setState('reading');
      }
    })();

    return () => {
      cancelled = true;
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, signerCifNumber]);

  // ── Scroll-progress tracking ───────────────────────────────────────────
  const recalcScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 0) {
      // Content fits without scrolling — treat as fully read.
      setScrollPct(100);
      return;
    }
    const pct = Math.min(100, Math.round((el.scrollTop / max) * 100));
    setScrollPct((prev) => Math.max(prev, pct));
  }, []);

  // Re-run once after the text mounts so the no-scroll-needed branch fires.
  useEffect(() => {
    if (state === 'reading') {
      // Wait one frame so the scroller has measurable dimensions.
      requestAnimationFrame(() => recalcScroll());
    }
  }, [state, recalcScroll]);

  const onScroll = useCallback(() => {
    recalcScroll();
  }, [recalcScroll]);

  const canAccept = scrollPct >= SCROLL_THRESHOLD_PCT;

  // ── Accept handler ─────────────────────────────────────────────────────
  async function handleAccept(): Promise<void> {
    if (!canAccept || state === 'submitting') return;
    setState('submitting');
    setError(null);

    const now = new Date().toISOString();
    const payload: EsignConsentRecordInput = {
      cartId,
      signerCifNumber,
      intentTimestamp: asIso8601(intentTimestampRef.current),
      signatureTimestamp: asIso8601(now),
      signerUserAgent: navigator.userAgent,
      signerViewportSize: `${window.innerWidth}x${window.innerHeight}`,
      scrollProgressPct: Math.min(100, Math.max(0, Math.round(scrollPct))),
    };

    try {
      const resp = await api.retail.signing.esign.recordConsent(payload);
      if (!isMountedRef.current) return;
      if (!resp.success) {
        setError(resp.error ?? 'We could not save your consent. Please try again.');
        setState('reading');
        return;
      }
      onPass();
    } catch (err) {
      if (!isMountedRef.current) return;
      const msg =
        err instanceof Error
          ? err.message
          : 'We could not save your consent. Please try again.';
      setError(msg);
      setState('reading');
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────

  const title = useMemo(
    () => consentText?.title ?? 'Before you sign — electronic signature consent',
    [consentText]
  );

  // While the status probe is in flight we render the modal so the
  // customer doesn't see the underlying surface flash through. If the
  // probe short-circuits to pass(), the parent unmounts us before paint.
  return (
    <div role="dialog" aria-label={title} style={overlayS}>
      <div style={cardS}>
        <div style={headerS}>
          <h2 style={titleS}>{title}</h2>
          <p style={introS}>
            Hi {signerDisplayName} — please read the consent below, then tap{' '}
            <em>I agree</em> to receive your records and disclosures
            electronically. If you&apos;d rather receive everything on paper,
            tap <em>I prefer paper</em> and we&apos;ll switch you to mail.
          </p>
        </div>

        <div style={progressBarWrapS}>
          <div style={progressBarTrackS}>
            <div style={progressBarFillS(scrollPct, canAccept)} />
          </div>
          <div style={progressLabelS}>
            {canAccept
              ? 'Thanks — you have read enough to continue.'
              : `${scrollPct}% read — scroll to the bottom to enable I agree`}
          </div>
        </div>

        {state === 'checking-status' || state === 'loading-text' ? (
          <div style={loadingS}>Loading the consent text…</div>
        ) : (
          <div
            ref={scrollerRef}
            style={scrollerS}
            onScroll={onScroll}
            tabIndex={0}
            aria-label="Consent text"
          >
            {consentText?.body ?? FALLBACK_CONSENT_BODY}
          </div>
        )}

        {error && (
          <div role="alert" style={errorS}>
            {error}
          </div>
        )}

        <div style={footerS}>
          <button
            type="button"
            style={ghostBtnS}
            onClick={onCancel}
            disabled={state === 'submitting'}
          >
            I prefer paper
          </button>
          <button
            type="button"
            style={canAccept && state !== 'submitting' ? primaryBtnS : disabledBtnS}
            disabled={!canAccept || state === 'submitting'}
            onClick={() => {
              void handleAccept();
            }}
          >
            {state === 'submitting'
              ? 'Saving…'
              : 'I agree to receive electronic records and signatures'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EsignConsentGate;
